import React, { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Float from "../../../components/float/Float";
import useOutsideDetector from "../../../hooks/useOutsideDetector";
import { BiSearch } from "react-icons/bi";
import InputTextComponent from "../../../components/forms/InputTextComponent";
import LoadingComponent from "../../../components/loading/LoadingComponent";

const rmsApiProxyRetry = require("../../../utils/api/rmsApiProxyRetry");
const translator = require("../../../utils/translator/translator");
const stringUtil = require("../../../utils/string/stringUtil");

const minCharCountToSearch = 5;

const SearchCustomerField = ({ defaultValue = "", onSelect = (value) => {} }) => {
     const [customers, setCustomers] = useState([]);
     const [inputText, setInputText] = useState(defaultValue);
     const [isOptionOpen, setIsOptionOpen] = useState(false);
     const [isFetchingCustomer, setIsFetchingCustomer] = useState(false);

     const [maxHeightForOptions, setMaxHeightForOptions] = useState(70);
     const firstOptionItemRef = useRef(null);

     const inputRef = useRef(null);

     const lastSearchRef = useRef(null);
     const lastSearchTimerRef = useRef(null);

     const containerRef = useRef(null);
     useOutsideDetector(containerRef, () => {
          setIsOptionOpen(false);
     });

     const handleInputValueChange = (value) => {
          setInputText(value);
          setIsOptionOpen(true);
     };

     const checkCustomerSearchInputValue = useCallback((value) => {
          const namePattern = /.*/;
          const phonePattern = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/;

          if (phonePattern.test(value)) {
               return "phoneNumber";
          } else if (namePattern.test(value)) {
               return "name";
          } else {
               return null;
          }
     }, []);

     const options = useMemo(() => customers.map((customer) => ({ data: customer, value: customer.id })), [customers]);

     const handleOnOptionClick = useCallback(
          (id) => {
               const foundCustomer = customers.find((customer) => customer.id === id);
               if (foundCustomer) {
                    onSelect(foundCustomer);
               }

               setIsOptionOpen(false);
          },
          [onSelect, customers]
     );

     const handleOnNewOptionClick = useCallback(
          (value) => {
               const detectedKey = checkCustomerSearchInputValue(value);
               if (detectedKey === null) return;

               if (detectedKey) {
                    onSelect({
                         [detectedKey]: value,
                    });
               }

               setIsOptionOpen(false);
          },
          [onSelect, checkCustomerSearchInputValue]
     );

     useEffect(() => {
          if (firstOptionItemRef.current) setMaxHeightForOptions(firstOptionItemRef.current.clientHeight);
     }, [options]);

     useEffect(() =>
     {
         let mounted = true;

         const clearSearchTimer = () =>
         {
             if (lastSearchTimerRef.current != null)
             {
                 clearTimeout(lastSearchTimerRef.current);
                 lastSearchTimerRef.current = null;
             }
         }

         const fetchCustomers = async (searchKey, searchText) =>
         {
             if (lastSearchTimerRef.current == null) return;    // Defensive coding

             const timerId = lastSearchTimerRef.current;

             setIsFetchingCustomer(true);

             let fetchedCustomer = [];

             try
             {
                 if(searchKey === "phoneNumber" && searchText.startsWith("0")) searchText = searchText.slice(1);

                 fetchedCustomer = await rmsApiProxyRetry.put(`${rmsApiProxyRetry.getFranchisorOrgContextUrl()}/customers/search`, {
                     [searchKey]: searchText,
                     includeFullRecordsOnly: true
                 });
             }
             catch (e)
             {
                 console.error("Failed while fetching customer: ", e);
             }

             if (mounted && timerId === lastSearchTimerRef.current) 
             {
                 // Implies mounted and same timerId
                 setIsFetchingCustomer(false);
                 setCustomers(fetchedCustomer);
                 lastSearchRef.current = { searchKey, searchText };
             }
         };

         // Implies user has modified inputText

         clearSearchTimer();
         setIsFetchingCustomer(false);

         const searchText = inputText;
         const searchKey = checkCustomerSearchInputValue(searchText);

         if (searchText.length < minCharCountToSearch || stringUtil.isStringNullOrEmpty(searchKey)) 
         {
             // Discard last search results
             lastSearchRef.current = null;
             setCustomers([]);
             return;
         }

         if (lastSearchRef.current != null) 
         {
             // Check if last search results are still valid for the new searchText

             if (
                 searchKey === lastSearchRef.current.searchKey &&
                 searchText.toLocaleLowerCase().includes(lastSearchRef.current.searchText.toLocaleLowerCase()))
             {
                 return;    // Implies last search is still valid
             }
         }

         // Initiate new search

         setCustomers([]);
         lastSearchRef.current = null;
         lastSearchTimerRef.current = setTimeout(() => fetchCustomers(searchKey, searchText), 500);

         return () =>
         {
             clearSearchTimer();
             mounted = false;
         };
     }, [inputText, checkCustomerSearchInputValue]);

     useEffect(() => {
          if (inputRef.current) inputRef.current.focus();
     }, []);

    const filteredOptions = lastSearchRef.current != null
        ? options.filter(option => option.data[lastSearchRef.current.searchKey].toLocaleLowerCase().includes(
          lastSearchRef.current.searchKey === "phoneNumber" && inputText.startsWith("0") 
               ? inputText.slice(1).toLocaleLowerCase() 
               : inputText.toLocaleLowerCase()))
        : options;

     return (
          <div ref={containerRef} className="position-relative">
               <InputTextComponent
                    autoComplete="off"
                    id="searchCustomer"
                    name="searchCustomer"
                    placeholder={
                         <span>
                              <BiSearch /> {translator.getLabel("search_customer_(name_/_phone_number)")}
                         </span>
                    }
                    onValueChange={handleInputValueChange}
                    value={inputText}
                    inputTextType="text"
                    internalRef={inputRef}
               />

               {((filteredOptions.length > 0 && inputText.length >= minCharCountToSearch) || inputText.length >= minCharCountToSearch) && isOptionOpen && (
                    <Float>
                         <div className="d-flex flex-column rounded bg-white shadow">
                              {isFetchingCustomer && (
                                   <div className="py-3">
                                        <LoadingComponent />
                                   </div>
                              )}
                              <div
                                   className={`${isFetchingCustomer ? "d-none" : "d-flex"} flex-column overflow-y-scroll`}
                                   style={{ maxHeight: maxHeightForOptions * 2.5 }}
                              >
                                   {filteredOptions.map((option, idx) => (
                                        <Item
                                             key={option.value}
                                             {...(idx === 0 ? { ref: firstOptionItemRef } : {})}
                                             item={option}
                                             onClick={handleOnOptionClick}
                                        />
                                   ))}
                              </div>
                              {!isFetchingCustomer && inputText.length > 2 && (
                                   <div
                                        className="btn text-start p-3 text-primary fs-6"
                                        style={{
                                             background: "var(--colors-primary20)",
                                             border: "1px solid #00000010",
                                             boxShadow: "0 -5px 20px #00000014",
                                        }}
                                        onClick={() => handleOnNewOptionClick(inputText)}
                                   >
                                        <div>{`${translator.getLabel("create_new_customer")} "${inputText}"`}</div>
                                   </div>
                              )}
                         </div>
                    </Float>
               )}
          </div>
     );
};

export default memo(SearchCustomerField);

const Item = memo(
     forwardRef(({ item, onClick }, ref = null) => {
          const handleOnClick = () => {
               onClick(item.value);
          };

         const address = stringUtil.truncateString(
             [item.data.address, item.data.address2, item.data.address3, item.data.address4]
                 .filter(x => !stringUtil.isStringNullOrEmpty(x))
                 .join(" "), 50);

         return (
             <div ref={ref} className="fs-6 btn px-3 py-2 text-start border-bottom" onClick={handleOnClick}>
                 <div>{item.data.name}</div>
                 <div className="d-flex">
                     {
                         !stringUtil.isStringNullOrEmpty(item.data.phoneNumber) &&
                         <div className="pe-2">{item.data.phoneNumber}</div>
                     }
                     {
                         !stringUtil.isStringNullOrEmpty(address) &&
                         <div>{`(${address})`}</div>
                     }
                 </div>
             </div>
         );
     })
);
